/**
 * Phase 8.2 — Run-long EOD continuation flow (pure planner).
 *
 * Doctrine:
 *   - Bucket 5 §5.x  At EOD with unresolved running/paused work, the
 *                    valid paths are:
 *                      1. Continue past EOD under explicit after-hours
 *                         confirmation (Phase 6.4).
 *                      2. Pause/stop and create a valid Part B
 *                         continuation.
 *                      3. Block until user resolves promise / physical
 *                         / bay / tech constraints.
 *                      4. Require authorized Due Out change OR breach
 *                         record if promise cannot be preserved.
 *   - Bucket 5 §5.x  A valid Part B continuation must:
 *                      - preserve Part A actuals + timer history
 *                      - represent remaining work clearly
 *                      - use technician-entered remaining time when
 *                        provided
 *                      - allow system-estimated fallback when missing
 *                      - schedule next valid day / bay / tech
 *                      - preserve Due Out OR require authorized edit
 *                      - protect Waiter promises
 *                      - not create impossible physical location
 *                      - not use locked-out bay
 *                      - not hide unfinished work
 *                      - not silently edit Due Out
 *                      - not auto-stop a timer without explicit action
 *   - Bucket 4.5 §4.5.5  Due Out outranks Waiter; physical outranks
 *                        Due Out (Phase 8.1 already enforces).
 *
 * Why this module exists
 * ----------------------
 * Read-first survey at Phase 8.2 confirmed:
 *
 *   - `eodRolloverHandler.ts:createEODRolloverSplit` is the canonical
 *     EOD continuation creator. It:
 *       - Derives `remainingMinutes = opti_time - todayMinutes`
 *         (always — no tech-entered source).
 *       - Persists `remaining_minutes_at_split` on Part A.
 *       - Updates Part A via `mutateTicketSchedule` writing only
 *         `opti_finish` + 6 companion fields — actuals
 *         (`actual_minutes`, `actual_start_time`, `stopped_at`) are
 *         preserved BY OMISSION (they're not in the update payload).
 *       - Picks compatible bay via `pickCompatibleBayForTech` (which
 *         already filters locked-out bays per Phase 7.9 + honors
 *         R-LOC-9 bay/parking mutex).
 *       - Hard-blocks Waiter rollover at L193-196.
 *       - Has standalone `validateDueOut` (L67-79) but does NOT
 *         auto-invoke it inside `createEODRolloverSplit`.
 *
 *   - **Gaps**:
 *       - No tech-entered remaining-time path.
 *       - No system-estimate fallback distinct from prediction-minus-
 *         actual.
 *       - No "prompt remaining time" UX surface.
 *       - No integration with Phase 6.4 `afterHoursConfirmationEscalation`.
 *       - No integration with Phase 8.1 `rolloverPromiseResolution`.
 *       - No end-to-end verdict that says "continue past EOD with
 *         after-hours confirmation, OR roll over, OR block".
 *
 * Phase 8.2 supplies a single end-to-end planner composing:
 *   - Phase 6.4 (after-hours state + grace + escalation).
 *   - Phase 8.1 (rollover promise risk + Due Out / Waiter / physical
 *     piping).
 *   - Phase 7.5 (rollover descendant guard via Phase 8.1).
 *   - Phase 7.7 (vehicle physical reality via Phase 8.1).
 *   - Phase 7.9 (bay lockout via Phase 8.1).
 *   - Phase 8.2-specific remaining-time source selection + Part A
 *     actuals preservation check.
 *
 * Phase 8.2 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     in-memory ticket projections + the proposed continuation
 *     context.
 *   - No DB migration / no schema change.
 *   - No runtime wiring. `eodRolloverHandler.ts`, `useEODMonitor`,
 *     `EODRolloverModal`, cascade, smart-assign, AutoBump are
 *     untouched.
 *
 * Builds on:
 *   - Phase 6.4 `afterHoursConfirmationEscalation.getAfterHoursWorkState`.
 *   - Phase 8.1 `rolloverPromiseResolution.getRolloverPromiseResolutionPlan`.
 *   - Phase 7.5 / 7.7 / 7.9 via Phase 8.1's composition.
 *   - Phase 1.1 `getProductionState`.
 */

import {
  getAfterHoursWorkState,
  type AfterHoursContext,
  type AfterHoursWorkState,
} from './afterHoursConfirmationEscalation';
import {
  getRolloverPromiseResolutionPlan,
  type RolloverPromiseTicketShape,
  type RolloverPromiseResolutionInput,
  type RolloverPromiseRiskState,
  type RolloverPromiseBlocker,
  type RolloverResolutionAction,
} from './rolloverPromiseResolution';
import type { BayLockoutShape } from './bayLockoutEnforcement';
import type { DueOutEditAuthorizedRole } from './dueOutEditAuthorization';
import {
  getProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';

// =============================================================================
// Vocabulary
// =============================================================================

/**
 * Discrete end-to-end verdicts.
 *
 *   - 'continue_past_eod_safe'           — after-hours confirmed OR
 *      tech is still within shift; no continuation needed.
 *   - 'requires_after_hours_confirmation' — past EOD, timer running,
 *      not yet confirmed; user must confirm before continuing.
 *   - 'after_hours_escalated'            — grace elapsed without
 *      confirmation; manager warning required.
 *   - 'create_part_b_continuation'       — tech wants to stop; Part B
 *      can be created with the resolved remaining time.
 *   - 'requires_remaining_time_prompt'   — tech needs to enter
 *      remaining time before Part B can be created.
 *   - 'uses_system_estimate_fallback'    — Part B will use a system-
 *      estimated remaining time (subkind of `create_part_b_continuation`).
 *   - 'requires_authorized_due_out_change' — Part B would violate
 *      Due Out; authorized actor must edit OR file breach.
 *   - 'requires_breach_record'           — explicit breach record path.
 *   - 'blocked_waiter_promise'           — Part B would violate Waiter
 *      promise; must reassign / convert / reschedule first.
 *   - 'blocked_due_out_promise'          — Part B would violate Due Out
 *      AND no authorized actor available.
 *   - 'blocked_locked_bay'               — next-day target bay locked
 *      out.
 *   - 'blocked_physical_reality'         — same vehicle in two places
 *      or invalid mixed location.
 *   - 'blocked_no_valid_next_window'     — no valid next working window
 *      within lookahead.
 *   - 'blocked_missing_context'          — caller didn't pass enough
 *      input to resolve.
 *   - 'block_until_resolved'             — generic block; surfaces
 *      blocker list for manual review.
 */
export type RunLongEODVerdict =
  | 'continue_past_eod_safe'
  | 'requires_after_hours_confirmation'
  | 'after_hours_escalated'
  | 'create_part_b_continuation'
  | 'requires_remaining_time_prompt'
  | 'uses_system_estimate_fallback'
  | 'requires_authorized_due_out_change'
  | 'requires_breach_record'
  | 'blocked_waiter_promise'
  | 'blocked_due_out_promise'
  | 'blocked_locked_bay'
  | 'blocked_physical_reality'
  | 'blocked_no_valid_next_window'
  | 'blocked_missing_context'
  | 'block_until_resolved';

export const RUN_LONG_EOD_VERDICTS = Object.freeze([
  'continue_past_eod_safe',
  'requires_after_hours_confirmation',
  'after_hours_escalated',
  'create_part_b_continuation',
  'requires_remaining_time_prompt',
  'uses_system_estimate_fallback',
  'requires_authorized_due_out_change',
  'requires_breach_record',
  'blocked_waiter_promise',
  'blocked_due_out_promise',
  'blocked_locked_bay',
  'blocked_physical_reality',
  'blocked_no_valid_next_window',
  'blocked_missing_context',
  'block_until_resolved',
] as const) satisfies ReadonlyArray<RunLongEODVerdict>;

/**
 * Discrete remaining-time sources (priority high to low):
 *   - 'technician_entered'            — tech entered explicit minutes.
 *   - 'system_estimated'              — caller-supplied AI / heuristic
 *                                       estimate (separate from raw
 *                                       prediction).
 *   - 'inherited_prediction_minus_actual' — `predicted_minutes -
 *                                       actual_minutes` (today's
 *                                       runtime default).
 *   - 'default_minimum'               — fallback minimum minutes when
 *                                       no source resolved a positive
 *                                       value.
 *   - 'missing'                       — nothing resolved; caller must
 *                                       prompt.
 */
export type RemainingTimeSource =
  | 'technician_entered'
  | 'system_estimated'
  | 'inherited_prediction_minus_actual'
  | 'default_minimum'
  | 'missing';

export const REMAINING_TIME_SOURCES = Object.freeze([
  'technician_entered',
  'system_estimated',
  'inherited_prediction_minus_actual',
  'default_minimum',
  'missing',
] as const) satisfies ReadonlyArray<RemainingTimeSource>;

/**
 * Discrete blocker codes (composed from Phase 6.4 / 7.5 / 7.7 / 7.9 /
 * 8.1 plus Phase 8.2-specific codes).
 */
export type RunLongEODBlocker =
  // Remaining-time channel.
  | 'missing_remaining_time'
  | 'missing_system_estimate'
  | 'invalid_remaining_time'
  // Part A integrity.
  | 'part_a_actuals_not_preserved'
  | 'timer_history_missing'
  // After-hours.
  | 'after_hours_confirmation_required'
  | 'after_hours_unconfirmed_escalated'
  // Promise risks (Phase 8.1).
  | 'waiter_promise_violation'
  | 'due_out_violation'
  | 'due_out_edit_authorization_required'
  | 'breach_record_required'
  // Physical / placement.
  | 'no_valid_next_work_window'
  | 'next_day_bay_locked_out'
  | 'next_day_tech_unavailable'
  | 'vehicle_physical_reality_conflict'
  | 'active_rollover_descendant_exists'
  // Structural / context.
  | 'missing_shop_hours'
  | 'missing_shift_window'
  | 'missing_rollover_context';

export const RUN_LONG_EOD_BLOCKERS = Object.freeze([
  'missing_remaining_time',
  'missing_system_estimate',
  'invalid_remaining_time',
  'part_a_actuals_not_preserved',
  'timer_history_missing',
  'after_hours_confirmation_required',
  'after_hours_unconfirmed_escalated',
  'waiter_promise_violation',
  'due_out_violation',
  'due_out_edit_authorization_required',
  'breach_record_required',
  'no_valid_next_work_window',
  'next_day_bay_locked_out',
  'next_day_tech_unavailable',
  'vehicle_physical_reality_conflict',
  'active_rollover_descendant_exists',
  'missing_shop_hours',
  'missing_shift_window',
  'missing_rollover_context',
] as const) satisfies ReadonlyArray<RunLongEODBlocker>;

/** Discrete recommended actions. All Catalyst entries are manual-only. */
export type RunLongEODAction =
  | 'prompt_remaining_time'
  | 'use_system_estimate'
  | 'preserve_part_a_actuals'
  | 'create_part_b_next_valid_window'
  | 'require_after_hours_confirmation'
  | 'warn_manager'
  | 'require_authorized_due_out_edit'
  | 'require_breach_record'
  | 'block_continuation'
  | 'allow_after_hours_continue'
  | 'manual_only';

export const RUN_LONG_EOD_ACTIONS = Object.freeze([
  'prompt_remaining_time',
  'use_system_estimate',
  'preserve_part_a_actuals',
  'create_part_b_next_valid_window',
  'require_after_hours_confirmation',
  'warn_manager',
  'require_authorized_due_out_edit',
  'require_breach_record',
  'block_continuation',
  'allow_after_hours_continue',
  'manual_only',
] as const) satisfies ReadonlyArray<RunLongEODAction>;

// =============================================================================
// Input shapes
// =============================================================================

/**
 * Caller's intent at EOD. Distinguishes the two doctrinal paths:
 *   - 'continue_after_hours' — tech wants to keep working past EOD.
 *   - 'stop_and_rollover'    — tech wants to stop; create Part B.
 *   - 'unspecified'          — caller has not chosen; helper resolves
 *                              to the most conservative path (prompt /
 *                              confirm).
 */
export type EODIntent =
  | 'continue_after_hours'
  | 'stop_and_rollover'
  | 'unspecified';

export interface RunLongEODContinuationInput {
  /** The parent ticket being evaluated. */
  parent: RolloverPromiseTicketShape;
  /** Reference clock — typically `now()` in shop tz. */
  now: Date;
  /** Caller's intent (continue-past-EOD vs stop-and-rollover). */
  intent: EODIntent;
  /** Shift end for the assigned tech. */
  shiftEnd?: Date | string | null;
  /** Optional shop close instant. */
  shopClose?: Date | string | null;
  /** Optional after-hours grace minutes (Phase 6.4 default 15). */
  graceMin?: number;
  /** True when the user has explicitly confirmed after-hours work. */
  afterHoursConfirmed?: boolean;
  /** When confirmation first became required (Phase 6.4). */
  unconfirmedSinceAt?: Date | string | null;
  /** Technician-entered remaining minutes (highest priority). */
  technicianEnteredRemainingMin?: number | null;
  /** Caller-supplied system-estimate remaining minutes (second priority). */
  systemEstimatedRemainingMin?: number | null;
  /** Optional minimum-minutes fallback. Default 1. */
  defaultMinimumRemainingMin?: number;
  /**
   * Optional projected Part B finish (caller pre-computes from
   * remainingMinutes + next-day start). When omitted, the planner
   * synthesises one from remaining + a default day offset; callers
   * that need exact projection should pass it.
   */
  projectedPartBFinishAt?: Date | string | null;
  /** Optional next-day window (Phase 6.4 `getNextValidWorkWindowStart` output). */
  nextValidWindowStartAt?: Date | string | null;
  /** Optional flag: no valid next workday found. */
  noValidNextWindow?: boolean;
  /** Optional next-day target bay (for lockout check). */
  targetBay?: BayLockoutShape | null;
  /** Optional flag: next-day tech unavailable. */
  nextDayTechUnavailable?: boolean;
  /** Optional ticket set (for physical-reality + descendant scans). */
  tickets?: ReadonlyArray<RolloverPromiseTicketShape> | null;
  /** Optional acting user role for Due Out edit authorization. */
  actorRole?: DueOutEditAuthorizedRole | string | null;
  /** Optional Waiter flex minutes. */
  waiterFlexMin?: number;
}

// =============================================================================
// Verdict
// =============================================================================

export interface RemainingTimeResolution {
  minutes: number;
  source: RemainingTimeSource;
}

export interface RunLongEODContinuationPlan {
  verdict: RunLongEODVerdict;
  blockers: RunLongEODBlocker[];
  actions: RunLongEODAction[];
  /** Resolved remaining time + source. */
  remainingTime: RemainingTimeResolution;
  /** True iff Part A actuals are preserved (or N/A). */
  partAActualsPreserved: boolean;
  /** Phase 6.4 verdict piped through. */
  afterHoursState: AfterHoursWorkState | null;
  /** Phase 8.1 verdict piped through. */
  rolloverPromiseState: RolloverPromiseRiskState | null;
  /** Phase 8.1 blockers piped through for transparency. */
  rolloverPromiseBlockers: RolloverPromiseBlocker[];
  /** Phase 8.1 actions piped through. */
  rolloverPromiseActions: RolloverResolutionAction[];
  /** True iff verdict is one of the safe-to-proceed states. */
  ok: boolean;
}

// =============================================================================
// Internal helpers
// =============================================================================

function pushUnique<T>(arr: T[], v: T): void {
  if (!arr.includes(v)) arr.push(v);
}

function parseTs(v: Date | string | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

function isPositiveFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0;
}

// =============================================================================
// Remaining-time source selection
// =============================================================================

/**
 * Select the highest-priority remaining-time source for Part B.
 *
 * Priority:
 *   1. `technician_entered` (positive finite minutes)
 *   2. `system_estimated` (positive finite minutes)
 *   3. `inherited_prediction_minus_actual` (positive finite)
 *   4. `default_minimum` (configurable; default 1 min)
 *   5. `missing` — nothing usable.
 */
export function selectRemainingTimeSource(
  input: RunLongEODContinuationInput,
): RemainingTimeResolution {
  if (isPositiveFiniteNumber(input.technicianEnteredRemainingMin)) {
    return { minutes: input.technicianEnteredRemainingMin, source: 'technician_entered' };
  }
  if (isPositiveFiniteNumber(input.systemEstimatedRemainingMin)) {
    return { minutes: input.systemEstimatedRemainingMin, source: 'system_estimated' };
  }

  // Prediction minus actual.
  const predicted =
    typeof input.parent?.predicted_minutes === 'number'
      ? input.parent.predicted_minutes
      : null;
  const actual =
    typeof input.parent?.actual_minutes === 'number'
      ? input.parent.actual_minutes
      : 0;
  if (predicted != null && Number.isFinite(predicted)) {
    const diff = Math.max(0, predicted - Math.max(0, actual));
    if (diff > 0) {
      return {
        minutes: diff,
        source: 'inherited_prediction_minus_actual',
      };
    }
  }

  // Default minimum fallback.
  const defMin = input.defaultMinimumRemainingMin;
  if (typeof defMin === 'number' && Number.isFinite(defMin) && defMin > 0) {
    return { minutes: defMin, source: 'default_minimum' };
  }

  return { minutes: 0, source: 'missing' };
}

/**
 * Sister: returns the raw `{minutes, source}` for a continuation —
 * thin alias matching the directive's `calculateRemainingWorkForContinuation`
 * vocabulary.
 */
export function calculateRemainingWorkForContinuation(
  input: RunLongEODContinuationInput,
): RemainingTimeResolution {
  return selectRemainingTimeSource(input);
}

// =============================================================================
// Part A actuals preservation
// =============================================================================

/**
 * Validate that the parent ticket carries Part A actuals that would
 * survive a rollover. Today's runtime preserves actuals BY OMISSION
 * (the rollover UPDATE writes only `opti_finish` + 6 companion
 * fields; actuals stay). This helper documents that contract and
 * surfaces gaps for callers (e.g., if `actual_start_time` is null,
 * timer history is missing).
 *
 * Returns:
 *   - `{ ok: true }` iff actuals are intact.
 *   - `{ ok: false, blockers }` listing the specific gaps.
 */
export function validatePartAActualsPreserved(
  parent: ProductionStateTicketShape | null | undefined,
): { ok: boolean; blockers: RunLongEODBlocker[] } {
  const blockers: RunLongEODBlocker[] = [];
  if (parent == null) {
    pushUnique(blockers, 'part_a_actuals_not_preserved');
    return { ok: false, blockers };
  }
  // Actuals = actual_start_time set + at least one of:
  //   actual_minutes (positive) OR stopped_at OR timer_status running/paused.
  const hasStart =
    (parent as { actual_start_time?: string | Date | null }).actual_start_time != null ||
    (parent as { actually_started?: boolean | null }).actually_started === true;
  const actualMinutes = (parent as { actual_minutes?: number | null }).actual_minutes;
  const hasMinutes = typeof actualMinutes === 'number' && actualMinutes > 0;
  const hasStop =
    (parent as { stopped_at?: string | Date | null }).stopped_at != null;
  const timerStatus = (parent as { timer_status?: string | null }).timer_status;
  const hasTimerHistory = hasStart || hasMinutes || hasStop;
  if (!hasTimerHistory) {
    pushUnique(blockers, 'timer_history_missing');
    pushUnique(blockers, 'part_a_actuals_not_preserved');
  }
  // A live timer doesn't strictly mean actuals are preserved — but
  // it does mean the runtime hasn't lost them yet. We treat
  // running/paused as "actuals in flight, not lost."
  if (
    !hasTimerHistory &&
    (timerStatus === 'running' || timerStatus === 'paused')
  ) {
    // Override: timer in flight, blockers cleared.
    blockers.length = 0;
  }
  return { ok: blockers.length === 0, blockers };
}

// =============================================================================
// Next-window helper
// =============================================================================

export interface NextValidContinuationWindow {
  hasWindow: boolean;
  startAt: string | null;
  blockers: RunLongEODBlocker[];
}

/**
 * Resolve the next valid continuation window from caller-supplied
 * fields. Pure — does not query shop hours. Callers compose Phase
 * 6.4 `getNextValidWorkWindowStart` upstream and pass the result via
 * `nextValidWindowStartAt`.
 *
 * Returns:
 *   - `hasWindow: false` + `no_valid_next_work_window` when caller
 *     explicitly flags `noValidNextWindow: true` OR provides no
 *     `nextValidWindowStartAt`.
 *   - `hasWindow: true` + ISO string otherwise.
 */
export function getNextValidContinuationWindow(
  input: RunLongEODContinuationInput,
): NextValidContinuationWindow {
  const blockers: RunLongEODBlocker[] = [];
  if (input.noValidNextWindow === true) {
    pushUnique(blockers, 'no_valid_next_work_window');
    return { hasWindow: false, startAt: null, blockers };
  }
  const startMs = parseTs(input.nextValidWindowStartAt);
  if (startMs == null) {
    pushUnique(blockers, 'no_valid_next_work_window');
    return { hasWindow: false, startAt: null, blockers };
  }
  return {
    hasWindow: true,
    startAt: new Date(startMs).toISOString(),
    blockers,
  };
}

// =============================================================================
// Part B placement validation
// =============================================================================

export interface PartBPlacementValidation {
  ok: boolean;
  blockers: RunLongEODBlocker[];
  rolloverPromiseState: RolloverPromiseRiskState;
  rolloverPromiseBlockers: RolloverPromiseBlocker[];
  rolloverPromiseActions: RolloverResolutionAction[];
}

/**
 * Validate the proposed Part B placement against Phase 8.1's
 * rollover promise planner (which itself composes Phase 7.5 /
 * 7.7 / 7.9). Also surfaces caller-flagged `nextDayTechUnavailable`.
 */
export function validatePartBContinuationPlacement(
  input: RunLongEODContinuationInput,
  remaining: RemainingTimeResolution,
): PartBPlacementValidation {
  const blockers: RunLongEODBlocker[] = [];

  if (input.nextDayTechUnavailable === true) {
    pushUnique(blockers, 'next_day_tech_unavailable');
  }

  // Phase 8.1 promise risk.
  const promiseInput: RolloverPromiseResolutionInput = {
    parent: input.parent,
    projectedPartBFinishAt: input.projectedPartBFinishAt,
    tickets: input.tickets,
    targetBay: input.targetBay,
    actorRole: input.actorRole ?? null,
    waiterFlexMin: input.waiterFlexMin,
  };
  const promise = getRolloverPromiseResolutionPlan(promiseInput);

  // Translate Phase 8.1 blockers into Phase 8.2 vocabulary.
  for (const b of promise.blockers) {
    if (b === 'rollover_exceeds_waiter_promise') pushUnique(blockers, 'waiter_promise_violation');
    else if (b === 'rollover_exceeds_due_out') pushUnique(blockers, 'due_out_violation');
    else if (b === 'due_out_edit_authorization_required') pushUnique(blockers, 'due_out_edit_authorization_required');
    else if (b === 'breach_record_required') pushUnique(blockers, 'breach_record_required');
    else if (b === 'active_rollover_descendant_exists') pushUnique(blockers, 'active_rollover_descendant_exists');
    else if (b === 'bay_lockout_blocks_rollover') pushUnique(blockers, 'next_day_bay_locked_out');
    else if (b === 'vehicle_physical_reality_conflict') pushUnique(blockers, 'vehicle_physical_reality_conflict');
    else if (b === 'missing_rollover_context') pushUnique(blockers, 'missing_rollover_context');
  }

  // Remaining-time check.
  if (remaining.source === 'missing') {
    pushUnique(blockers, 'missing_remaining_time');
  } else if (remaining.minutes <= 0) {
    pushUnique(blockers, 'invalid_remaining_time');
  }

  return {
    ok: blockers.length === 0,
    blockers,
    rolloverPromiseState: promise.state,
    rolloverPromiseBlockers: promise.blockers,
    rolloverPromiseActions: promise.actions,
  };
}

// =============================================================================
// Public planner
// =============================================================================

/**
 * Build the end-to-end run-long EOD continuation plan. Pure. Does
 * not mutate inputs.
 *
 * Resolution ladder (worst-case state wins):
 *
 *   1. Missing parent → `blocked_missing_context` + `missing_rollover_context`.
 *   2. Phase 6.4 after-hours state with `missing_shift_window` →
 *      `blocked_missing_context` + `missing_shift_window`.
 *   3. Phase 6.4 returns `within_hours` AND intent !== 'stop_and_rollover'
 *      → `continue_past_eod_safe`.
 *   4. Phase 6.4 returns `after_hours_escalated` → verdict
 *      `after_hours_escalated` + `warn_manager`.
 *   5. Phase 6.4 returns `after_hours_unconfirmed` / `after_hours_grace`
 *      AND intent !== 'stop_and_rollover' →
 *      `requires_after_hours_confirmation` + `require_after_hours_confirmation`.
 *   6. Phase 6.4 returns `after_hours_confirmed` AND intent !==
 *      'stop_and_rollover' → `continue_past_eod_safe` + `allow_after_hours_continue`.
 *   7. Intent is `stop_and_rollover` OR Phase 6.4 says
 *      `off_shift_not_running` (timer already stopped past EOD):
 *      a. Resolve remaining-time source.
 *         - If `missing` → `requires_remaining_time_prompt` +
 *           `prompt_remaining_time`.
 *         - If `system_estimated` → set verdict
 *           `uses_system_estimate_fallback` (downstream still resolves
 *           to `create_part_b_continuation` if no blockers).
 *      b. Resolve next-valid window.
 *         - Missing → `blocked_no_valid_next_window` +
 *           `no_valid_next_work_window`.
 *      c. Validate Part A actuals.
 *         - Missing → `part_a_actuals_not_preserved` blocker.
 *      d. Validate Part B placement (Phase 8.1 piping).
 *         - Bay lockout → `blocked_locked_bay`.
 *         - Active descendant / vehicle reality / waiter / due out →
 *           specific verdicts.
 *      e. If everything OK → `create_part_b_continuation` +
 *         `create_part_b_next_valid_window` + `preserve_part_a_actuals`.
 *
 * Bucket 4.5 §4.5.5 invariant: Due Out outranks Waiter (Phase 8.1
 * handles this). Physical constraints outrank promise risks (Phase 8.1
 * handles this).
 */
export function getRunLongEODContinuationPlan(
  input: RunLongEODContinuationInput,
): RunLongEODContinuationPlan {
  const blockers: RunLongEODBlocker[] = [];
  const actions: RunLongEODAction[] = [];

  if (input == null || input.parent == null) {
    return {
      verdict: 'blocked_missing_context',
      blockers: ['missing_rollover_context'],
      actions: ['manual_only'],
      remainingTime: { minutes: 0, source: 'missing' },
      partAActualsPreserved: false,
      afterHoursState: null,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: false,
    };
  }

  // Phase 1.1 terminal exclusion: if parent is picked_up / deleted /
  // archived / Cancelled → safe (no continuation needed).
  const productionState = getProductionState(input.parent);
  if (
    productionState === 'picked_up' ||
    productionState === 'deleted' ||
    productionState === 'archived' ||
    input.parent.status === 'Cancelled'
  ) {
    return {
      verdict: 'continue_past_eod_safe',
      blockers,
      actions: ['manual_only'],
      remainingTime: { minutes: 0, source: 'missing' },
      partAActualsPreserved: true,
      afterHoursState: null,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: true,
    };
  }

  // Phase 6.4 after-hours state.
  const afterHoursContext: AfterHoursContext = {
    now: input.now,
    shiftEnd: input.shiftEnd,
    shopClose: input.shopClose,
    isTimerRunning: input.parent.timer_status === 'running',
    afterHoursConfirmed: input.afterHoursConfirmed,
    unconfirmedSinceAt: input.unconfirmedSinceAt,
    graceMin: input.graceMin,
  };
  const ah = getAfterHoursWorkState(afterHoursContext);

  if (ah.blockers.includes('missing_shift_window')) {
    pushUnique(blockers, 'missing_shift_window');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'blocked_missing_context',
      blockers,
      actions,
      remainingTime: { minutes: 0, source: 'missing' },
      partAActualsPreserved: false,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: false,
    };
  }

  const wantsStop = input.intent === 'stop_and_rollover';

  // 3. Within shift → safe (no continuation needed; tech can still
  // work normally).
  if (ah.state === 'within_hours' && !wantsStop) {
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'continue_past_eod_safe',
      blockers,
      actions,
      remainingTime: selectRemainingTimeSource(input),
      partAActualsPreserved: validatePartAActualsPreserved(input.parent).ok,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: true,
    };
  }

  // 4. After-hours escalated — only short-circuits when the tech has
  //    NOT chosen to stop. If intent === 'stop_and_rollover', the
  //    tech is actively stopping the after-hours work to do a clean
  //    rollover; the continuation path should win over the escalation
  //    warning. The escalation banner stays informational for the
  //    UI but doesn't gate the rollover.
  if (ah.state === 'after_hours_escalated' && !wantsStop) {
    pushUnique(blockers, 'after_hours_unconfirmed_escalated');
    pushUnique(actions, 'warn_manager');
    pushUnique(actions, 'require_after_hours_confirmation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'after_hours_escalated',
      blockers,
      actions,
      remainingTime: selectRemainingTimeSource(input),
      partAActualsPreserved: validatePartAActualsPreserved(input.parent).ok,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: false,
    };
  }

  // 5. Unconfirmed / grace (timer running past shift, not yet
  // confirmed) AND tech hasn't chosen to stop → confirm first.
  if (
    (ah.state === 'after_hours_unconfirmed' ||
      ah.state === 'after_hours_grace') &&
    !wantsStop
  ) {
    pushUnique(blockers, 'after_hours_confirmation_required');
    pushUnique(actions, 'require_after_hours_confirmation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'requires_after_hours_confirmation',
      blockers,
      actions,
      remainingTime: selectRemainingTimeSource(input),
      partAActualsPreserved: validatePartAActualsPreserved(input.parent).ok,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: false,
    };
  }

  // 6. Confirmed after-hours and tech wants to keep going → allow.
  if (ah.state === 'after_hours_confirmed' && !wantsStop) {
    pushUnique(actions, 'allow_after_hours_continue');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'continue_past_eod_safe',
      blockers,
      actions,
      remainingTime: selectRemainingTimeSource(input),
      partAActualsPreserved: validatePartAActualsPreserved(input.parent).ok,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: true,
    };
  }

  // 7. Continuation path (intent === stop_and_rollover OR timer
  // already stopped past EOD).
  const remaining = selectRemainingTimeSource(input);

  // 7a. Missing remaining time → prompt.
  if (remaining.source === 'missing') {
    pushUnique(blockers, 'missing_remaining_time');
    pushUnique(actions, 'prompt_remaining_time');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'requires_remaining_time_prompt',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: validatePartAActualsPreserved(input.parent).ok,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: false,
    };
  }

  // 7b. Invalid (<= 0) — defensive.
  if (remaining.minutes <= 0) {
    pushUnique(blockers, 'invalid_remaining_time');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'blocked_missing_context',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: validatePartAActualsPreserved(input.parent).ok,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: false,
    };
  }

  // 7c. Part A actuals.
  const partA = validatePartAActualsPreserved(input.parent);
  for (const b of partA.blockers) pushUnique(blockers, b);

  // 7d. Next valid window.
  const window = getNextValidContinuationWindow(input);
  if (!window.hasWindow) {
    for (const b of window.blockers) pushUnique(blockers, b);
    pushUnique(actions, 'block_continuation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'blocked_no_valid_next_window',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: partA.ok,
      afterHoursState: ah.state,
      rolloverPromiseState: null,
      rolloverPromiseBlockers: [],
      rolloverPromiseActions: [],
      ok: false,
    };
  }

  // 7e. Placement validation (Phase 8.1 + Phase 8.2 channels).
  const placement = validatePartBContinuationPlacement(input, remaining);
  for (const b of placement.blockers) pushUnique(blockers, b);

  // Determine the specific blocked verdict from the worst-case
  // blocker. Phase 8.1 already orders physical > Due Out > Waiter, so
  // the first matching blocker (in priority order) wins.
  if (placement.blockers.includes('vehicle_physical_reality_conflict')) {
    pushUnique(actions, 'block_continuation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'blocked_physical_reality',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: partA.ok,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: false,
    };
  }
  if (placement.blockers.includes('active_rollover_descendant_exists')) {
    pushUnique(actions, 'block_continuation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'blocked_physical_reality',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: partA.ok,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: false,
    };
  }
  if (placement.blockers.includes('next_day_bay_locked_out')) {
    pushUnique(actions, 'block_continuation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'blocked_locked_bay',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: partA.ok,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: false,
    };
  }
  if (placement.blockers.includes('next_day_tech_unavailable')) {
    pushUnique(actions, 'block_continuation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'block_until_resolved',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: partA.ok,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: false,
    };
  }
  if (placement.blockers.includes('due_out_violation')) {
    if (placement.blockers.includes('due_out_edit_authorization_required')) {
      pushUnique(actions, 'require_authorized_due_out_edit');
      pushUnique(actions, 'require_breach_record');
      pushUnique(actions, 'block_continuation');
      pushUnique(actions, 'manual_only');
      return {
        verdict: 'requires_authorized_due_out_change',
        blockers,
        actions,
        remainingTime: remaining,
        partAActualsPreserved: partA.ok,
        afterHoursState: ah.state,
        rolloverPromiseState: placement.rolloverPromiseState,
        rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
        rolloverPromiseActions: placement.rolloverPromiseActions,
        ok: false,
      };
    }
    // Authorized actor present — still suggestion-only.
    pushUnique(actions, 'require_authorized_due_out_edit');
    pushUnique(actions, 'require_breach_record');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'requires_authorized_due_out_change',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: partA.ok,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: false,
    };
  }
  if (placement.blockers.includes('waiter_promise_violation')) {
    pushUnique(actions, 'block_continuation');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'blocked_waiter_promise',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: partA.ok,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: false,
    };
  }

  // 7f. Part A actuals issue is structural — block.
  if (!partA.ok) {
    pushUnique(actions, 'preserve_part_a_actuals');
    pushUnique(actions, 'manual_only');
    return {
      verdict: 'block_until_resolved',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: false,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: false,
    };
  }

  // 7g. All clear → create Part B. If the remaining-time source is
  // system_estimated, the verdict surfaces the subkind for caller
  // routing (UI may want to flag "we estimated this — confirm?").
  pushUnique(actions, 'preserve_part_a_actuals');
  pushUnique(actions, 'create_part_b_next_valid_window');
  pushUnique(actions, 'manual_only');
  if (remaining.source === 'system_estimated') {
    pushUnique(actions, 'use_system_estimate');
    return {
      verdict: 'uses_system_estimate_fallback',
      blockers,
      actions,
      remainingTime: remaining,
      partAActualsPreserved: true,
      afterHoursState: ah.state,
      rolloverPromiseState: placement.rolloverPromiseState,
      rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
      rolloverPromiseActions: placement.rolloverPromiseActions,
      ok: true,
    };
  }

  return {
    verdict: 'create_part_b_continuation',
    blockers,
    actions,
    remainingTime: remaining,
    partAActualsPreserved: true,
    afterHoursState: ah.state,
    rolloverPromiseState: placement.rolloverPromiseState,
    rolloverPromiseBlockers: placement.rolloverPromiseBlockers,
    rolloverPromiseActions: placement.rolloverPromiseActions,
    ok: true,
  };
}

// =============================================================================
// Convenience wrappers
// =============================================================================

/** Single-verdict convenience wrapper. */
export function getRunLongEODContinuationVerdict(
  input: RunLongEODContinuationInput,
): RunLongEODVerdict {
  return getRunLongEODContinuationPlan(input).verdict;
}

/** Flat blocker list. */
export function getRunLongEODBlockers(
  input: RunLongEODContinuationInput,
): RunLongEODBlocker[] {
  return getRunLongEODContinuationPlan(input).blockers;
}

/** Flat action list. */
export function getRunLongEODActions(
  input: RunLongEODContinuationInput,
): RunLongEODAction[] {
  return getRunLongEODContinuationPlan(input).actions;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. **useEODMonitor / EODRolloverModal preflight**:
//    Before invoking `createEODRolloverSplit`, call
//    `getRunLongEODContinuationPlan({parent, now, intent,
//    shiftEnd, afterHoursConfirmed, technicianEnteredRemainingMin,
//    nextValidWindowStartAt, targetBay, actorRole})`:
//      - `verdict === 'continue_past_eod_safe'` → no rollover yet;
//        let the timer run.
//      - `verdict === 'requires_after_hours_confirmation'` →
//        surface the after-hours modal.
//      - `verdict === 'after_hours_escalated'` → render manager
//        warning banner.
//      - `verdict === 'requires_remaining_time_prompt'` → open the
//        remaining-time prompt UI before invoking rollover.
//      - `verdict === 'uses_system_estimate_fallback'` → optionally
//        ask "we estimated X minutes — confirm?" before proceeding.
//      - `verdict === 'create_part_b_continuation'` → proceed with
//        `createEODRolloverSplit` using `plan.remainingTime.minutes`.
//      - blocked verdicts → block + surface the blocker list.
//
// 2. **Tech-entered remaining time prompt**:
//    A new `<EODRemainingTimePrompt>` component captures
//    `technicianEnteredRemainingMin` before invoking rollover. The
//    planner's `requires_remaining_time_prompt` verdict drives the
//    modal open state.
//
// 3. **System-estimate provider**:
//    A future Phase 8.3+ component can compute a richer system
//    estimate (using AI / regression on prior similar tickets) and
//    pass it as `systemEstimatedRemainingMin`. Today the runtime
//    falls through to `inherited_prediction_minus_actual` —
//    `selectRemainingTimeSource` preserves the priority order, so
//    adoption is non-breaking.
//
// 4. **Audit**: reserve
//    `RUN_LONG_EOD_CONTINUATION_BLOCKED_PROMISE_RISK`,
//    `RUN_LONG_EOD_CONTINUATION_SYSTEM_ESTIMATE_USED`,
//    `RUN_LONG_EOD_CONTINUATION_TECH_TIME_ENTERED` in
//    `AdminActionType` once adoption emits audit rows.
//
// 5. **Phase 8.3+ runtime adoption**:
//    `createEODRolloverSplit` can be wrapped with a thin pre-flight
//    that consults this planner and refuses to proceed when verdict
//    is blocked. The runtime continues to handle the actual writes;
//    the planner handles the policy. Source-scan tests would pin
//    the integration.
